import { useCallback, useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { Paperclip, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Checkbox } from "@/components/ui/checkbox";
import { PageHeader, Panel, Empty } from "@/components/app/PageParts";
import { ActionDialog } from "@/components/app/ActionDialog";
import { PropertyFilter, PropertyTag } from "@/components/app/PropertySelect";
import { api, errorMessage } from "@/lib/api";

const ActionRow = ({ a, onToggle, onDelete, properties }) => (
  <li data-testid={`action-row-${a.id}`} className="group flex items-start gap-4 px-5 py-4">
    <Checkbox checked={a.status === "done"} onCheckedChange={() => onToggle(a)} data-testid="action-item-checkbox" className="mt-0.5 h-5 w-5 data-[state=checked]:bg-gold data-[state=checked]:border-gold data-[state=checked]:text-navy" />
    <div className="min-w-0 flex-1">
      <p className={`text-sm font-medium ${a.status === "done" ? "text-slate-400 line-through" : "text-navy"}`} data-testid="action-title">{a.title}</p>
      {a.notes && <p className="mt-0.5 text-sm text-slate-500">{a.notes}</p>}
      <PropertyTag properties={properties} id={a.property_id} />
      {a.document_name && (
        <p className="mt-1 inline-flex items-center gap-1 text-xs text-slate-500">
          <Paperclip className="h-3 w-3" /> {a.document_name}{a.document_deleted && <span className="text-red-600"> (deleted)</span>}
        </p>
      )}
    </div>
    <button onClick={() => onDelete(a)} data-testid="action-delete-button" aria-label="Delete action" className="rounded p-1.5 text-slate-400 hover:bg-red-50 hover:text-red-700">
      <Trash2 className="h-4 w-4" />
    </button>
  </li>
);

export default function Actions() {
  const { workspace, properties } = useOutletContext();
  const [items, setItems] = useState(null);
  const [filter, setFilter] = useState(null);
  const base = `/workspaces/${workspace.id}/actions`;

  const load = useCallback(() => {
    api.get(base, { params: { property_id: filter || undefined } }).then(({ data }) => setItems(data.actions)).catch((e) => toast.error(errorMessage(e)));
  }, [base, filter]);
  useEffect(load, [load]);

  const toggle = async (a) => {
    try { await api.patch(`${base}/${a.id}`, { status: a.status === "done" ? "open" : "done" }); load(); } catch (e) { toast.error(errorMessage(e)); }
  };
  const remove = async (a) => {
    try { await api.delete(`${base}/${a.id}`); toast.success("Action deleted"); load(); } catch (e) { toast.error(errorMessage(e)); }
  };

  const open = items?.filter((a) => a.status === "open") || [];
  const done = items?.filter((a) => a.status === "done") || [];
  return (
    <div data-testid="actions-page">
      <PageHeader eyebrow="Next steps" title="Actions" subtitle="Write down what your paperwork asks you to do, then check it off."
        action={<ActionDialog workspaceId={workspace.id} properties={properties} onSaved={load} />} />
      <PropertyFilter properties={properties} value={filter} onChange={setFilter} />
      <Panel title={`To do (${open.length})`} testId="actions-open-list">
        {items && !open.length ? <Empty testId="actions-open-empty">Nothing open. Add an action to get started.</Empty>
          : <ul className="divide-y">{open.map((a) => <ActionRow key={a.id} a={a} onToggle={toggle} onDelete={remove} properties={properties} />)}</ul>}
      </Panel>
      <Panel title={`Done (${done.length})`} className="mt-6" testId="actions-done-list">
        {items && !done.length ? <Empty testId="actions-done-empty">No finished actions yet.</Empty>
          : <ul className="divide-y">{done.map((a) => <ActionRow key={a.id} a={a} onToggle={toggle} onDelete={remove} properties={properties} />)}</ul>}
      </Panel>
    </div>
  );
}
